import { useState } from "react";
import { Form, required, useNotify, useTranslate } from "ra-core";
import { useSetPassword, useSupabaseAccessToken } from "ra-supabase-core";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import {
  AuthShell,
  authButtonClassName,
  authInputClassName,
} from "@/components/atomic-crm/login/AuthShell";

interface SetPasswordFormData {
  password: string;
  confirmPassword: string;
}

export const SetPasswordPage = () => {
  const [loading, setLoading] = useState(false);

  const access_token = useSupabaseAccessToken();
  const refresh_token = useSupabaseAccessToken({
    parameterName: "refresh_token",
  });

  const notify = useNotify();
  const translate = useTranslate();
  const [, { mutateAsync: setPassword }] = useSetPassword();

  const validate = (values: SetPasswordFormData) => {
    if (values.password !== values.confirmPassword) {
      return {
        password: "ra-supabase.validation.password_mismatch",
        confirmPassword: "ra-supabase.validation.password_mismatch",
      };
    }
    return {};
  };

  if (!access_token || !refresh_token) {
    if (process.env.NODE_ENV === "development") {
      console.error("Missing access_token or refresh_token for set password");
    }
    return (
      <AuthShell>
        <p>{translate("ra-supabase.auth.missing_tokens")}</p>
      </AuthShell>
    );
  }

  const submit = async (values: SetPasswordFormData) => {
    try {
      setLoading(true);
      await setPassword({
        access_token,
        refresh_token,
        password: values.password,
      });
    } catch {
      notify("crm.auth.generic_error", {
        type: "warning",
        messageArgs: {
          _: "Something went wrong. Please try again.",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="space-y-6">
        <h2 className="font-heading text-2xl font-extrabold tracking-tight">
          {translate("ra-supabase.set_password.new_password", {
            _: "Choose your password",
          })}
        </h2>
        <Form
          className="space-y-4"
          onSubmit={submit as any}
          validate={validate as any}
        >
          <TextInput
            label={translate("ra.auth.password", {
              _: "Password",
            })}
            autoComplete="new-password"
            source="password"
            type="password"
            inputClassName={authInputClassName}
            validate={required()}
          />
          <TextInput
            label={translate("crm.auth.confirm_password", {
              _: "Confirm password",
            })}
            source="confirmPassword"
            type="password"
            inputClassName={authInputClassName}
            validate={required()}
          />
          <Button
            type="submit"
            className={authButtonClassName}
            disabled={loading}
          >
            {translate("ra.action.save")}
          </Button>
        </Form>
      </div>
    </AuthShell>
  );
};

SetPasswordPage.path = "set-password";
